import {css} from "@panda/css";

import {CenterBox, Container} from "@pg-design/grid-module";
import {Loader} from "@pg-design/loader-module";
import {IOption, Select} from "@pg-design/select-module";
import {Text} from "@pg-design/text-module";
import {areaFormat, priceFormat} from "@pg-mono/string-utils";

import {PriceComparisonCard} from "../../atoms/PriceComparisonCard";
import {
    getDefaultRoomsFilter,
    getRoomsFlatCostVerb,
    getRoomsFlatsLabelWhere,
    getRoomsTabTitle,
    getRoomsTypicalFlatLabel
} from "../../constants/prices_address_rooms";
import {isAllRooms, RoomsFilter} from "../../constants/rcn_rooms_filter";
import {AddressRoomsStats, useAddressRoomsStats} from "../../hooks/use_address_rooms_stats";
import {useRcnLatestStats} from "../../hooks/use_rcn_latest_stats";
import {PRICES_ADDRESS_ANALYTICS_LOCATION} from "../../tracking/prices_city_analytics";
import type {IPricesAddress} from "../../types/IPricesAddress";
import {formatRcnPointPeriod} from "../../utils/format_rcn_period_label";
import type {IRcnResolvedPoint} from "../../utils/resolve_rcn_latest_point";
import {PricesCityViewSectionTracker} from "../PricesCityViewSectionTracker";

interface Props {
    address: IPricesAddress;
    rooms: RoomsFilter;
    availableRooms: RoomsFilter[];
    onRoomsChange: (rooms: RoomsFilter) => void;
}

interface IRoomsMedian {
    rooms: number;
    value: number;
}

interface IRoomsSpread {
    highest: IRoomsMedian;
    lowest: IRoomsMedian;
    diff: number;
}

const RCN_SOURCE = "Źródło: akty notarialne z Rejestru Cen Nieruchomości";

export const PricesAddressRoomsSection = (props: Props) => {
    const {address, rooms, availableRooms, onRoomsChange} = props;

    const perM2Stats = useRcnLatestStats(address.rcnAddress, rooms, "per_m2");
    const totalStats = useRcnLatestStats(address.rcnAddress, rooms, "total");
    const perM2 = perM2Stats.point;
    const roomsStats = useAddressRoomsStats(address.rcnAddress, availableRooms);
    // The spread only compares sizes from the period (and grain) of the opening filter's headline - the one the
    // page opens with, whichever size is picked now - so it never sets a 2022 median against a 2025 one.
    const openingStats = useRcnLatestStats(address.rcnAddress, getDefaultRoomsFilter(availableRooms), "per_m2");

    const total = totalStats.point;
    const isLoading = perM2Stats.isLoading || totalStats.isLoading;
    const spread = getRoomsSpread(roomsStats, openingStats.point && openingStats.grain ? {point: openingStats.point, grain: openingStats.grain} : null);

    const introSentences = [
        "Cena zależy przede wszystkim od rozmiaru mieszkania.",
        spread && `Pod tym adresem różnica między najmniejszymi a największymi mieszkaniami sięga ${priceFormat(spread.diff, {precision: 0})} na metrze.`,
        "Wybierz rozmiar, żeby zobaczyć dokładne ceny."
    ].filter(Boolean);

    const roomsOptions: IOption<RoomsFilter>[] = availableRooms.map((option) => ({label: getRoomsTabTitle(option), value: option}));
    const selectedRoomsOption = roomsOptions.find((option) => option.value === rooms) ?? null;

    return (
        <Container className={containerStyle}>
            <PricesCityViewSectionTracker sectionName="address_prices" location={PRICES_ADDRESS_ANALYTICS_LOCATION} />

            <div className={headerStyle}>
                <div className={headerTextStyle}>
                    <Text variant="headline_3" as="h2">
                        Ile płacono za mieszkanie pod tym adresem?
                    </Text>

                    <Text variant="body_copy_1" color="var(--colors-gray-700)">
                        {introSentences.join(" ")}
                    </Text>
                </div>

                <Select
                    aria-label="Rozmiar mieszkania"
                    labelContent="Rozmiar mieszkania"
                    className={selectStyle}
                    options={roomsOptions}
                    value={selectedRoomsOption}
                    onChange={(option) => onRoomsChange((option as IOption<RoomsFilter>).value)}
                    isSearchable={false}
                />
            </div>

            {isLoading ? (
                <CenterBox height="24rem">
                    <Loader size="lg" />
                </CenterBox>
            ) : (
                <div className={cardsRowStyle}>
                    {perM2 ? (
                        <PriceComparisonCard
                            variant="white"
                            title="Cena za metr"
                            priceM2={Math.round(perM2.value)}
                            description={`Mediana z ${perM2.n_rows} transakcji — tyle płacono za metr ${getRoomsFlatsLabelWhere(rooms)}.`}
                            source={`${RCN_SOURCE} · ${formatRcnPointPeriod(perM2)}`}
                        />
                    ) : (
                        <PriceComparisonCard
                            variant="white"
                            title="Cena za metr"
                            priceM2={null}
                            description={getMissingDescription(rooms, "cenę za metr")}
                            source={RCN_SOURCE}
                        />
                    )}

                    {total ? (
                        <PriceComparisonCard
                            variant="beige"
                            title="Cena całego mieszkania"
                            price={Math.round(total.value)}
                            description={getTotalDescription(rooms, total.value, perM2?.value)}
                            source={`Mediana z ${total.n_rows} transakcji · ${formatRcnPointPeriod(total)}`}
                        />
                    ) : (
                        <PriceComparisonCard
                            variant="beige"
                            title="Cena całego mieszkania"
                            price={null}
                            description={getMissingDescription(rooms, "cenę całego mieszkania")}
                            source={RCN_SOURCE}
                        />
                    )}
                </div>
            )}
        </Container>
    );
};

// "w tym segmencie" only means something once a size is picked; on the all-flats view there is none.
const getMissingDescription = (rooms: RoomsFilter, what: string): string =>
    isAllRooms(rooms) ? `Za mało transakcji, żeby pokazać ${what} pod tym adresem.` : `Za mało transakcji, żeby pokazać ${what} w tym segmencie.`;

// Sizes whose newest point is of another period than `opening` are left out - their medians describe other sales.
const getRoomsSpread = (stats: AddressRoomsStats, opening: IRcnResolvedPoint | null): IRoomsSpread | null => {
    if (!opening) {
        return null;
    }

    const medians: IRoomsMedian[] = [...stats.entries()]
        .filter(([, {point, grain}]) => grain === opening.grain && point.period_start === opening.point.period_start)
        .map(([rooms, {point}]) => ({rooms, value: point.value}));

    if (medians.length < 2) {
        return null;
    }

    const highest = medians.reduce((max, median) => (median.value > max.value ? median : max));
    const lowest = medians.reduce((min, median) => (median.value < min.value ? median : min));
    const diff = Math.round(highest.value - lowest.value);

    return diff >= 1 ? {highest, lowest, diff} : null;
};

const getTotalDescription = (rooms: RoomsFilter, totalValue: number, valueM2: number | undefined): string => {
    const lead = `Tyle ${getRoomsFlatCostVerb(rooms)} ${getRoomsTypicalFlatLabel(rooms)} w tym budynku`;

    return valueM2 ? `${lead} — ma zwykle około ${areaFormat(totalValue / valueM2, {precision: 0})}.` : `${lead}.`;
};

const containerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    my: 4.5,
    maxWidth: "1168px",

    md: {
        my: 8
    }
});

const headerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,

    md: {
        flexDirection: "row",
        alignItems: "flex-start"
    }
});

const headerTextStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    minWidth: 0,

    md: {
        flex: "1 1 auto"
    }
});

const selectStyle = css({
    width: "100%",

    md: {
        width: "223px",
        flexShrink: 0
    }
});

const cardsRowStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,

    md: {
        flexDirection: "row"
    }
});
import {css} from "@panda/css";

import {CenterBox, Container} from "@pg-design/grid-module";
import {Loader} from "@pg-design/loader-module";
import {IOption, Select} from "@pg-design/select-module";
import {Text} from "@pg-design/text-module";
import {areaFormat, priceFormat} from "@pg-mono/string-utils";

import {PriceComparisonCard} from "../../atoms/PriceComparisonCard";
import {
    getDefaultRoomsFilter,
    getRoomsFlatCostVerb,
    getRoomsFlatsLabelWhere,
    getRoomsTabTitle,
    getRoomsTypicalFlatLabel
} from "../../constants/prices_address_rooms";
import {isAllRooms, RoomsFilter} from "../../constants/rcn_rooms_filter";
import {AddressRoomsStats, useAddressRoomsStats} from "../../hooks/use_address_rooms_stats";
import {useRcnLatestStats} from "../../hooks/use_rcn_latest_stats";
import {PRICES_ADDRESS_ANALYTICS_LOCATION} from "../../tracking/prices_city_analytics";
import type {IPricesAddress} from "../../types/IPricesAddress";
import {formatRcnPointPeriod} from "../../utils/format_rcn_period_label";
import type {IRcnResolvedPoint} from "../../utils/resolve_rcn_latest_point";
import {PricesCityViewSectionTracker} from "../PricesCityViewSectionTracker";

interface Props {
    address: IPricesAddress;
    rooms: RoomsFilter;
    availableRooms: RoomsFilter[];
    onRoomsChange: (rooms: RoomsFilter) => void;
}

interface IRoomsMedian {
    rooms: number;
    value: number;
}

interface IRoomsSpread {
    highest: IRoomsMedian;
    lowest: IRoomsMedian;
    diff: number;
}

const RCN_SOURCE = "Źródło: akty notarialne z Rejestru Cen Nieruchomości";

export const PricesAddressRoomsSection = (props: Props) => {
    const {address, rooms, availableRooms, onRoomsChange} = props;

    const perM2Stats = useRcnLatestStats(address.rcnAddress, rooms, "per_m2");
    const totalStats = useRcnLatestStats(address.rcnAddress, rooms, "total");
    const perM2 = perM2Stats.point;
    const roomsStats = useAddressRoomsStats(address.rcnAddress, availableRooms);
    // The spread only compares sizes from the period (and grain) of the opening filter's headline - the one the
    // page opens with, whichever size is picked now - so it never sets a 2022 median against a 2025 one.
    const openingStats = useRcnLatestStats(address.rcnAddress, getDefaultRoomsFilter(availableRooms), "per_m2");

    const total = totalStats.point;
    const isLoading = perM2Stats.isLoading || totalStats.isLoading;
    const spread = getRoomsSpread(roomsStats, openingStats.point && openingStats.grain ? {point: openingStats.point, grain: openingStats.grain} : null);

    const introSentences = [
        "Cena zależy przede wszystkim od rozmiaru mieszkania.",
        spread && `Pod tym adresem różnica między najmniejszymi a największymi mieszkaniami sięga ${priceFormat(spread.diff, {precision: 0})} na metrze.`,
        "Wybierz rozmiar, żeby zobaczyć dokładne ceny."
    ].filter(Boolean);

    const roomsOptions: IOption<RoomsFilter>[] = availableRooms.map((option) => ({label: getRoomsTabTitle(option), value: option}));
    const selectedRoomsOption = roomsOptions.find((option) => option.value === rooms) ?? null;

    return (
        <Container className={containerStyle}>
            <PricesCityViewSectionTracker sectionName="address_prices" location={PRICES_ADDRESS_ANALYTICS_LOCATION} />

            <div className={headerStyle}>
                <div className={headerTextStyle}>
                    <Text variant="headline_3" as="h2">
                        Ile płacono za mieszkanie pod tym adresem?
                    </Text>

                    <Text variant="body_copy_1" color="var(--colors-gray-700)">
                        {introSentences.join(" ")}
                    </Text>
                </div>

                <Select
                    aria-label="Rozmiar mieszkania"
                    labelContent="Rozmiar mieszkania"
                    className={selectStyle}
                    options={roomsOptions}
                    value={selectedRoomsOption}
                    onChange={(option) => onRoomsChange((option as IOption<RoomsFilter>).value)}
                    isSearchable={false}
                />
            </div>

            {isLoading ? (
                <CenterBox height="24rem">
                    <Loader size="lg" />
                </CenterBox>
            ) : (
                <div className={cardsRowStyle}>
                    {perM2 ? (
                        <PriceComparisonCard
                            variant="white"
                            title="Cena za metr"
                            priceM2={Math.round(perM2.value)}
                            description={`Mediana z ${perM2.n_rows} transakcji — tyle płacono za metr ${getRoomsFlatsLabelWhere(rooms)}.`}
                            source={`${RCN_SOURCE} · ${formatRcnPointPeriod(perM2)}`}
                        />
                    ) : (
                        <PriceComparisonCard
                            variant="white"
                            title="Cena za metr"
                            priceM2={null}
                            description={getMissingDescription(rooms, "cenę za metr")}
                            source={RCN_SOURCE}
                        />
                    )}

                    {total ? (
                        <PriceComparisonCard
                            variant="beige"
                            title="Cena całego mieszkania"
                            price={Math.round(total.value)}
                            description={getTotalDescription(rooms, total.value, perM2?.value)}
                            source={`Mediana z ${total.n_rows} transakcji · ${formatRcnPointPeriod(total)}`}
                        />
                    ) : (
                        <PriceComparisonCard
                            variant="beige"
                            title="Cena całego mieszkania"
                            price={null}
                            description={getMissingDescription(rooms, "cenę całego mieszkania")}
                            source={RCN_SOURCE}
                        />
                    )}
                </div>
            )}
        </Container>
    );
};

// "w tym segmencie" only means something once a size is picked; on the all-flats view there is none.
const getMissingDescription = (rooms: RoomsFilter, what: string): string =>
    isAllRooms(rooms) ? `Za mało transakcji, żeby pokazać ${what} pod tym adresem.` : `Za mało transakcji, żeby pokazać ${what} w tym segmencie.`;

// Sizes whose newest point is of another period than `opening` are left out - their medians describe other sales.
const getRoomsSpread = (stats: AddressRoomsStats, opening: IRcnResolvedPoint | null): IRoomsSpread | null => {
    if (!opening) {
        return null;
    }

    const medians: IRoomsMedian[] = [...stats.entries()]
        .filter(([, {point, grain}]) => grain === opening.grain && point.period_start === opening.point.period_start)
        .map(([rooms, {point}]) => ({rooms, value: point.value}));

    if (medians.length < 2) {
        return null;
    }

    const highest = medians.reduce((max, median) => (median.value > max.value ? median : max));
    const lowest = medians.reduce((min, median) => (median.value < min.value ? median : min));
    const diff = Math.round(highest.value - lowest.value);

    return diff >= 1 ? {highest, lowest, diff} : null;
};

const getTotalDescription = (rooms: RoomsFilter, totalValue: number, valueM2: number | undefined): string => {
    const lead = `Tyle ${getRoomsFlatCostVerb(rooms)} ${getRoomsTypicalFlatLabel(rooms)} w tym budynku`;

    return valueM2 ? `${lead} — ma zwykle około ${areaFormat(totalValue / valueM2, {precision: 0})}.` : `${lead}.`;
};

const containerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    my: 4.5,
    maxWidth: "1168px",

    md: {
        my: 8
    }
});

const headerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,

    md: {
        flexDirection: "row",
        alignItems: "flex-start"
    }
});

const headerTextStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    minWidth: 0,

    md: {
        flex: "1 1 auto"
    }
});

const selectStyle = css({
    width: "100%",

    md: {
        width: "223px",
        flexShrink: 0
    }
});

const cardsRowStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,

    md: {
        flexDirection: "row"
    }
});
